import React, { useCallback, useEffect, useState } from "react";
import { processDate, processDateJS } from "../../utils/dateProcessor";
import CustomTooltipIcon from "../customTooltipIcon/customTooltipIcon";
import { Button, CalendarPicker, Input, Select } from "../ReadonlyAwareInputs";
import {
  formatUtr,
  getAllowedPoaPeriodEndDates,
  isAllowedConsolidatedPartnershipDate,
  normalizeConsolidatedPartnerships,
  validateConsolidatedPartnershipDateByMode,
} from "./consolidatedPartnershipUtils";
import {
  DeletePartnershipWarningModal,
  EditDisposalDateWarningModal,
  EditStartDateWarningModal,
  MainEditDeleteWarningModal,
} from "./WarningModals";

const HEADER_TOOLTIPS = {
  name: "Enter the name of the specified consolidated partnership to which the interest allowance (consolidated partnership) election should apply.",
  utr: "Enter the Self-Assessment UTR of the specified consolidated partnership to which the interest allowance (consolidated partnership) election should apply. Under a Notice issued by HMRC under Schedule 7A TIOPA 2010 that has the force of law, it is a requirement for groups to state the Self-Assessment UTRs of UK partnerships in respect of which the group has made an interest allowance (consolidated partnerships) election under paragraph 18, Schedule 7A of TIOPA 2010.",
  startDate:
    "Enter the end of the first Period of Account in which the consolidated partnership election first applied to this partnership (i.e. the specified period of account for this partnership).",
  notPresent:
    "Where a consolidated partnership has been disposed off / dissolved prior to the start of the period, it does not need to be reported in the CIR return (as it no longer exists in the group). Use this column to identify the first Period of Account in which partnership is no longer part of the group.",
};

const renderHeadingWithTooltip = (label, tooltipText) => {
  return (
    <div
      style={{ display: "inline-flex", alignItems: "flex-start", gap: "4px" }}
    >
      <span>{label}</span>
      <CustomTooltipIcon tooltipText={tooltipText} />
    </div>
  );
};

/**
 * ConsolidatedPartnerships Component
 *
 * A reusable component for managing consolidated partnerships list with edit/delete capabilities.
 *
 * Props:
 * @param {Array} partnerships - Array of partnership objects with {name, utr, date, disposedDate, electionSubmitted}
 * @param {Function} onChange - Callback when partnerships change: (updatedPartnerships) => void
 * @param {string} defaultDate - Default date for new partnerships (optional)
 * @param {boolean} isReadOnly - Whether the component is in read-only mode
 * @param {boolean} showDateColumn - Whether to show the date column (default: true)
 * @param {boolean} showDatePicker - Whether to show date picker in groupConfig mode (default: false)
 * @param {boolean} isGroupConfig - Whether in group config mode (shows date picker inline, default: false)
 * @param {boolean} isGroupDataPage - Whether in group data page mode (enables new election workflow, default: false)
 * @param {Function} canEdit - Callback to determine if a row can be edited/deleted: (partnership, index) => boolean
 */
const ConsolidatedPartnerships = ({
  partnerships = [],
  onChange,
  defaultDate = "",
  isReadOnly = false,
  showDateColumn = true,
  showDatePicker = false,
  isGroupConfig = false,
  isGroupDataPage = false,
  poaPeriodEndDates = [],
  modelFirstPeriodStartDate,
  canEdit = () => true, // Default: all rows editable
}) => {
  // Normalize input data to handle old formats
  const normalizedPartnerships =
    normalizeConsolidatedPartnerships(partnerships);

  // Local state for immediate UI feedback (tracking user edits before save)
  const [localState, setLocalState] = useState({});

  // State for managing modals and edit mode (Group Data page only)
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editingStartDateIndex, setEditingStartDateIndex] = useState(null);
  const [editingDisposedDateIndex, setEditingDisposedDateIndex] =
    useState(null);
  const [startDateEditState, setStartDateEditState] = useState({});
  const [disposedDateEditState, setDisposedDateEditState] = useState({});
  const [modalState, setModalState] = useState({
    showEditStartDate: false,
    showMainEditDelete: false,
    showEditDisposalDate: false,
    showDeletePartnership: false,
    pendingAction: null,
  });

  // Reset local state when partnerships prop changes (e.g., after save or period change)
  useEffect(() => {
    setLocalState({});
    setStartDateEditState({});
    setDisposedDateEditState({});
    setEditingStartDateIndex(null);
    setEditingDisposedDateIndex(null);
  }, [partnerships]);

  const allowedPoaPeriodEndDates =
    getAllowedPoaPeriodEndDates(poaPeriodEndDates);

  const firstModelStartDate = modelFirstPeriodStartDate
    ? processDateJS(modelFirstPeriodStartDate)
    : null;
  const manualStartDateMax = firstModelStartDate
    ? firstModelStartDate.subtract(1, "day")
    : null;

  const isAllowedStartDate = useCallback(
    (dateValue) => {
      return isAllowedConsolidatedPartnershipDate(
        dateValue,
        allowedPoaPeriodEndDates,
        firstModelStartDate,
      );
    },
    [allowedPoaPeriodEndDates, firstModelStartDate],
  );

  const validateStartDateByMode = useCallback(
    (dateValue, mode) => {
      return validateConsolidatedPartnershipDateByMode(
        dateValue,
        mode,
        allowedPoaPeriodEndDates,
        firstModelStartDate,
        "Start date",
      );
    },
    [allowedPoaPeriodEndDates, firstModelStartDate],
  );

  const validateDisposedDateByMode = useCallback(
    (dateValue, mode) => {
      return validateConsolidatedPartnershipDateByMode(
        dateValue,
        mode,
        allowedPoaPeriodEndDates,
        firstModelStartDate,
        "Disposed date",
      );
    },
    [allowedPoaPeriodEndDates, firstModelStartDate],
  );

  // Check if any partnership has election submitted (for showing disposed date column)
  const hasAnyElectionSubmitted = normalizedPartnerships.some(
    (p) => p.electionSubmitted === true,
  );

  // Handle name change with local state
  const handleNameChange = useCallback(
    (index, value, partnership) => {
      // Allow changes if:
      // 1. Not readonly, AND
      // 2. Either canEdit returns true OR row is in edit mode (for submitted elections)
      const isInEditMode = editingRowIndex === index;
      if (isReadOnly || (!canEdit(partnership, index) && !isInEditMode)) return;

      // Update local state for immediate UI feedback
      setLocalState((prev) => ({
        ...prev,
        [index]: value,
      }));
    },
    [isReadOnly, canEdit, editingRowIndex],
  );

  // Handle UTR change (Group Data page only)
  const handleUTRChange = useCallback(
    (index, value) => {
      if (isReadOnly) return;

      const updatedPartnerships = [...normalizedPartnerships];
      updatedPartnerships[index] = {
        ...updatedPartnerships[index],
        utr: formatUtr(value),
      };
      onChange(updatedPartnerships);
    },
    [isReadOnly, normalizedPartnerships, onChange],
  );

  // Handle date change (only in groupConfig mode)
  const handleDateChange = useCallback(
    (index, value) => {
      if (isReadOnly) return;

      const updatedPartnerships = [...normalizedPartnerships];
      updatedPartnerships[index] = {
        ...updatedPartnerships[index],
        date: processDate(value),
      };
      onChange(updatedPartnerships);
    },
    [isReadOnly, normalizedPartnerships, onChange],
  );

  // Handle start date change (Group Data page only)
  const handleStartDateChange = useCallback(
    (index, value) => {
      const updatedPartnerships = [...normalizedPartnerships];
      updatedPartnerships[index] = {
        ...updatedPartnerships[index],
        date: processDate(value),
      };
      onChange(updatedPartnerships);
      setEditingStartDateIndex(null);
    },
    [normalizedPartnerships, onChange],
  );

  const beginStartDateEdit = useCallback(
    (index, currentDate) => {
      const normalizedCurrentDate = currentDate ? processDate(currentDate) : "";
      const currentIsPoaDate = normalizedCurrentDate
        ? allowedPoaPeriodEndDates.some((poaEnd) =>
            processDateJS(poaEnd)?.isSame(
              processDateJS(normalizedCurrentDate),
              "day",
            ),
          )
        : false;

      const mode = currentIsPoaDate ? "poa" : "manual";
      const defaultPoaDate = allowedPoaPeriodEndDates[0] || "";

      setStartDateEditState((prev) => ({
        ...prev,
        [index]: {
          mode: !currentIsPoaDate && !firstModelStartDate ? "poa" : mode,
          draftDate:
            normalizedCurrentDate ||
            (!currentIsPoaDate && !firstModelStartDate && defaultPoaDate
              ? defaultPoaDate
              : ""),
          error: "",
        },
      }));
      setEditingStartDateIndex(index);
    },
    [allowedPoaPeriodEndDates, firstModelStartDate],
  );

  const updateStartDateMode = useCallback(
    (index, mode) => {
      setStartDateEditState((prev) => {
        const current = prev[index] || {
          mode: "poa",
          draftDate: allowedPoaPeriodEndDates[0] || "",
          error: "",
        };

        let draftDate = current.draftDate;
        if (mode === "poa" && !isAllowedStartDate(draftDate)) {
          draftDate = allowedPoaPeriodEndDates[0] || "";
        }

        return {
          ...prev,
          [index]: {
            ...current,
            mode,
            draftDate,
            error: "",
          },
        };
      });
    },
    [allowedPoaPeriodEndDates, isAllowedStartDate],
  );

  const updateStartDateDraft = useCallback((index, draftDate) => {
    setStartDateEditState((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        draftDate,
        error: "",
      },
    }));
  }, []);

  const cancelStartDateEdit = useCallback((index) => {
    setStartDateEditState((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setEditingStartDateIndex(null);
  }, []);

  const applyStartDateEdit = useCallback(
    (index) => {
      const rowState = startDateEditState[index];
      const selectedMode = rowState?.mode || "poa";
      const selectedDate = rowState?.draftDate;

      const validation = validateStartDateByMode(selectedDate, selectedMode);
      if (!validation.isValid) {
        setStartDateEditState((prev) => ({
          ...prev,
          [index]: {
            ...(prev[index] || {}),
            error: validation.error,
          },
        }));
        return;
      }

      handleStartDateChange(index, selectedDate);
      setStartDateEditState((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    },
    [handleStartDateChange, startDateEditState, validateStartDateByMode],
  );

  // Handle disposed date change (after warning modal)
  const handleDisposedDateChange = useCallback(
    (index, value) => {
      const updatedPartnerships = [...normalizedPartnerships];
      updatedPartnerships[index] = {
        ...updatedPartnerships[index],
        disposedDate: processDate(value),
      };
      onChange(updatedPartnerships);
      setEditingDisposedDateIndex(null);
    },
    [normalizedPartnerships, onChange],
  );

  const beginDisposedDateEdit = useCallback(
    (index, currentDate) => {
      const normalizedCurrentDate = currentDate ? processDate(currentDate) : "";
      const currentIsPoaDate = normalizedCurrentDate
        ? allowedPoaPeriodEndDates.some((poaEnd) =>
            processDateJS(poaEnd)?.isSame(
              processDateJS(normalizedCurrentDate),
              "day",
            ),
          )
        : false;

      const mode = currentIsPoaDate ? "poa" : "manual";
      const defaultPoaDate = allowedPoaPeriodEndDates[0] || "";

      setDisposedDateEditState((prev) => ({
        ...prev,
        [index]: {
          mode: !currentIsPoaDate && !firstModelStartDate ? "poa" : mode,
          draftDate:
            normalizedCurrentDate ||
            (!currentIsPoaDate && !firstModelStartDate && defaultPoaDate
              ? defaultPoaDate
              : ""),
          error: "",
        },
      }));
      setEditingDisposedDateIndex(index);
    },
    [allowedPoaPeriodEndDates, firstModelStartDate],
  );

  const updateDisposedDateMode = useCallback(
    (index, mode) => {
      setDisposedDateEditState((prev) => {
        const current = prev[index] || {
          mode: "poa",
          draftDate: allowedPoaPeriodEndDates[0] || "",
          error: "",
        };

        let draftDate = current.draftDate;
        if (mode === "poa" && !isAllowedStartDate(draftDate)) {
          draftDate = allowedPoaPeriodEndDates[0] || "";
        }

        return {
          ...prev,
          [index]: {
            ...current,
            mode,
            draftDate,
            error: "",
          },
        };
      });
    },
    [allowedPoaPeriodEndDates, isAllowedStartDate],
  );

  const updateDisposedDateDraft = useCallback((index, draftDate) => {
    setDisposedDateEditState((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        draftDate,
        error: "",
      },
    }));
  }, []);

  const cancelDisposedDateEdit = useCallback((index) => {
    setDisposedDateEditState((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setEditingDisposedDateIndex(null);
  }, []);

  const applyDisposedDateEdit = useCallback(
    (index) => {
      const rowState = disposedDateEditState[index];
      const selectedMode = rowState?.mode || "poa";
      const selectedDate = rowState?.draftDate;

      const validation = validateDisposedDateByMode(selectedDate, selectedMode);
      if (!validation.isValid) {
        setDisposedDateEditState((prev) => ({
          ...prev,
          [index]: {
            ...(prev[index] || {}),
            error: validation.error,
          },
        }));
        return;
      }

      handleDisposedDateChange(index, selectedDate);
      setDisposedDateEditState((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    },
    [
      disposedDateEditState,
      handleDisposedDateChange,
      validateDisposedDateByMode,
    ],
  );

  // Handle delete
  const handleDelete = useCallback(
    (index, partnership) => {
      // Allow delete if not readonly
      // For electionSubmitted=false: delete directly
      // For electionSubmitted=true: delete only if user confirmed the warning modal (which calls this function)
      if (
        isReadOnly ||
        editingStartDateIndex !== null ||
        editingDisposedDateIndex !== null
      )
        return;

      // Immediately remove from array
      const updatedPartnerships = normalizedPartnerships.filter(
        (_, idx) => idx !== index,
      );
      onChange(updatedPartnerships);

      // Clear local state for deleted item and reindex remaining items
      setLocalState((prev) => {
        const newState = {};
        Object.keys(prev).forEach((key) => {
          const keyIndex = parseInt(key);
          if (keyIndex < index) {
            // Keep items before deleted index
            newState[keyIndex] = prev[key];
          } else if (keyIndex > index) {
            // Shift items after deleted index down by 1
            newState[keyIndex - 1] = prev[key];
          }
          // Skip the deleted index
        });
        return newState;
      });
    },
    [
      isReadOnly,
      normalizedPartnerships,
      onChange,
      editingStartDateIndex,
      editingDisposedDateIndex,
    ],
  );

  // Modal handlers for Group Data page
  const openModal = useCallback((modalType, action = null) => {
    setModalState((prev) => ({
      ...prev,
      [modalType]: true,
      pendingAction: action,
    }));
  }, []);

  const closeModal = useCallback((modalType) => {
    setModalState((prev) => ({
      ...prev,
      [modalType]: false,
      pendingAction: null,
    }));
  }, []);

  const handleModalConfirm = useCallback(
    (modalType) => {
      const { pendingAction } = modalState;
      if (pendingAction) {
        pendingAction();
      }
      closeModal(modalType);
    },
    [modalState, closeModal],
  );

  // Handle add new partnership
  const handleAdd = useCallback(() => {
    if (isReadOnly) return;

    // Use processDate to ensure consistent UTC format without timezone shifts
    const processedDate = defaultDate ? processDate(defaultDate) : null;
    const newEntry = {
      name: "",
      utr: "",
      date: processedDate,
      disposedDate: "",
      electionSubmitted: false,
    };
    const updatedPartnerships = [...normalizedPartnerships, newEntry];
    onChange(updatedPartnerships);

    // Initialize local state for new entry
    setLocalState((prev) => ({
      ...prev,
      [normalizedPartnerships.length]: "",
    }));
  }, [isReadOnly, defaultDate, normalizedPartnerships, onChange]);

  // Handle blur to commit changes to parent
  const handleBlur = useCallback(
    (index) => {
      // Only commit if there's a change in local state for this index
      if (localState[index] === undefined) return;

      const updatedPartnerships = normalizedPartnerships.map(
        (partnership, idx) => {
          // Apply local state changes for any index that has been edited
          if (localState[idx] !== undefined) {
            return { ...partnership, name: localState[idx] };
          }
          return partnership;
        },
      );

      onChange(updatedPartnerships);
    },
    [localState, normalizedPartnerships, onChange],
  );

  // Get display name (from local state or original data)
  const getDisplayName = useCallback(
    (index, originalName) => {
      // Use local state if available, otherwise original
      return localState[index] ?? (originalName || "");
    },
    [localState],
  );

  // Render Group Data page row (with electionSubmitted logic)
  const renderGroupDataRow = useCallback(
    (row, idx) => {
      const displayName = getDisplayName(idx, row.name);
      const isRowEditable = canEdit(row, idx) && !row.electionSubmitted;
      const isElectionSubmitted = row.electionSubmitted || false;
      const isInEditMode = editingRowIndex === idx;
      const isDateEditInProgress =
        editingStartDateIndex !== null || editingDisposedDateIndex !== null;
      const isAnotherRowDateEditInProgress =
        (editingStartDateIndex !== null && editingStartDateIndex !== idx) ||
        (editingDisposedDateIndex !== null && editingDisposedDateIndex !== idx);

      // Determine field editability
      const canEditName = isInEditMode && isElectionSubmitted;
      const canEditUTR =
        !isElectionSubmitted || (isInEditMode && isElectionSubmitted);
      const currentStartDateEditState = startDateEditState[idx] || {
        mode: "poa",
        draftDate: row.date ? processDate(row.date) : "",
        error: "",
      };
      const currentDisposedDateEditState = disposedDateEditState[idx] || {
        mode: "poa",
        draftDate: row.disposedDate ? processDate(row.disposedDate) : "",
        error: "",
      };
      const customModeDisabled = !firstModelStartDate;

      return (
        <tr key={`cp-${idx}`}>
          <td style={{ width: "25%", verticalAlign: "top" }}>
            <Input
              title="Consolidated partnership name"
              value={displayName}
              hideTitleOnInput
              onChange={(val) => handleNameChange(idx, val, row)}
              onBlur={() => handleBlur(idx)}
              disabled={isReadOnly || (isElectionSubmitted && !isInEditMode)}
              error={!displayName || displayName.trim() === ""}
            />
          </td>
          <td style={{ width: "10%", verticalAlign: "top" }}>
            <Input
              title="UTR"
              value={row.utr || ""}
              hideTitleOnInput
              onChange={(val) => handleUTRChange(idx, val)}
              disabled={isReadOnly || (isElectionSubmitted && !isInEditMode)}
              maxLength={11}
              pattern="[0-9-]*"
            />
          </td>
          <td style={{ width: "20%", verticalAlign: "top" }}>
            {editingStartDateIndex === idx ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Select
                  title="Select Period of Account in Analyser"
                  hideTitleOnInput
                  data={[
                    "Select Period of Account in Analyser",
                    "Enter Period of Account prior to Analyser",
                  ]}
                  value={
                    currentStartDateEditState.mode === "manual"
                      ? "Enter Period of Account prior to Analyser"
                      : "Select Period of Account in Analyser"
                  }
                  onSelect={(selectedMode) =>
                    updateStartDateMode(
                      idx,
                      selectedMode ===
                        "Enter Period of Account prior to Analyser"
                        ? "manual"
                        : "poa",
                    )
                  }
                />

                {currentStartDateEditState.mode === "poa" ? (
                  <Select
                    title="Select Period of Account"
                    hideTitleOnInput
                    data={allowedPoaPeriodEndDates.map((date) =>
                      processDateJS(date).format("DD/MM/YYYY"),
                    )}
                    value={
                      currentStartDateEditState.draftDate
                        ? processDateJS(
                            currentStartDateEditState.draftDate,
                          ).format("DD/MM/YYYY")
                        : ""
                    }
                    onSelect={(value) => {
                      const selectedDate = allowedPoaPeriodEndDates.find(
                        (date) =>
                          processDateJS(date).format("DD/MM/YYYY") === value,
                      );
                      updateStartDateDraft(idx, selectedDate || "");
                    }}
                    error={!currentStartDateEditState.draftDate}
                  />
                ) : (
                  <>
                    <CalendarPicker
                      title="Start Date"
                      fieldWidth={170}
                      value={
                        currentStartDateEditState.draftDate
                          ? new Date(currentStartDateEditState.draftDate)
                          : undefined
                      }
                      hideTitleOnInput
                      onChange={(value) => {
                        updateStartDateDraft(
                          idx,
                          value ? processDate(value) : "",
                        );
                      }}
                      maxDate={manualStartDateMax}
                      disabled={customModeDisabled}
                      format="DD/MM/YYYY"
                      placeholder="dd/mm/yyyy"
                    />
                    <small style={{ color: "#666" }}>
                      {firstModelStartDate
                        ? `Custom date must be before ${firstModelStartDate.format("DD/MM/YYYY")}.`
                        : "Custom date unavailable because first model Period of Account start date is missing."}
                    </small>
                  </>
                )}

                {currentStartDateEditState.error && (
                  <small style={{ color: "#d83b01" }}>
                    {currentStartDateEditState.error}
                  </small>
                )}

                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    kind="primary"
                    size="small"
                    onClick={() => applyStartDateEdit(idx)}
                  >
                    Apply
                  </Button>
                  <Button
                    kind="secondary"
                    size="small"
                    onClick={() => cancelStartDateEdit(idx)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{ width: "170px", minWidth: "170px", flexShrink: 0 }}
                >
                  <CalendarPicker
                    title="Start Date"
                    fieldWidth={170}
                    value={row.date ? new Date(row.date) : undefined}
                    hideTitleOnInput
                    onChange={(val) => handleStartDateChange(idx, val)}
                    disabled={true}
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                {!isElectionSubmitted && (
                  <Button
                    kind="secondary"
                    size="small"
                    onClick={() =>
                      openModal("showEditStartDate", () => {
                        beginStartDateEdit(idx, row.date);
                      })
                    }
                    disabled={isReadOnly || isAnotherRowDateEditInProgress}
                  >
                    Edit
                  </Button>
                )}
                {isElectionSubmitted && isInEditMode && (
                  <Button
                    kind="secondary"
                    size="small"
                    onClick={() =>
                      openModal("showEditStartDate", () => {
                        beginStartDateEdit(idx, row.date);
                      })
                    }
                    disabled={isReadOnly || isAnotherRowDateEditInProgress}
                  >
                    Edit
                  </Button>
                )}
              </div>
            )}
          </td>
          <td style={{ width: "20%", verticalAlign: "top" }}>
            {isElectionSubmitted ? (
              editingDisposedDateIndex === idx ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <Select
                    title="Select Period of Account in Analyser"
                    hideTitleOnInput
                    data={[
                      "Select Period of Account in Analyser",
                      "Enter Period of Account prior to Analyser",
                    ]}
                    value={
                      currentDisposedDateEditState.mode === "manual"
                        ? "Enter Period of Account prior to Analyser"
                        : "Select Period of Account in Analyser"
                    }
                    onSelect={(selectedMode) =>
                      updateDisposedDateMode(
                        idx,
                        selectedMode ===
                          "Enter Period of Account prior to Analyser"
                          ? "manual"
                          : "poa",
                      )
                    }
                  />

                  {currentDisposedDateEditState.mode === "poa" ? (
                    <Select
                      title="Select PoA period end date"
                      hideTitleOnInput
                      data={allowedPoaPeriodEndDates.map((date) =>
                        processDateJS(date).format("DD/MM/YYYY"),
                      )}
                      value={
                        currentDisposedDateEditState.draftDate
                          ? processDateJS(
                              currentDisposedDateEditState.draftDate,
                            ).format("DD/MM/YYYY")
                          : ""
                      }
                      onSelect={(value) => {
                        const selectedDate = allowedPoaPeriodEndDates.find(
                          (date) =>
                            processDateJS(date).format("DD/MM/YYYY") === value,
                        );
                        updateDisposedDateDraft(idx, selectedDate || "");
                      }}
                      error={!currentDisposedDateEditState.draftDate}
                    />
                  ) : (
                    <>
                      <CalendarPicker
                        title="Disposed Date"
                        fieldWidth={170}
                        value={
                          currentDisposedDateEditState.draftDate
                            ? new Date(currentDisposedDateEditState.draftDate)
                            : undefined
                        }
                        hideTitleOnInput
                        onChange={(value) => {
                          updateDisposedDateDraft(
                            idx,
                            value ? processDate(value) : "",
                          );
                        }}
                        maxDate={manualStartDateMax}
                        disabled={customModeDisabled}
                        format="DD/MM/YYYY"
                        placeholder="dd/mm/yyyy"
                      />
                      <small style={{ color: "#666" }}>
                        {firstModelStartDate
                          ? `Custom date must be before ${firstModelStartDate.format("DD/MM/YYYY")}.`
                          : "Custom date unavailable because first model Period of Account start date is missing."}
                      </small>
                    </>
                  )}

                  {currentDisposedDateEditState.error && (
                    <small style={{ color: "#d83b01" }}>
                      {currentDisposedDateEditState.error}
                    </small>
                  )}

                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      kind="primary"
                      size="small"
                      onClick={() => applyDisposedDateEdit(idx)}
                    >
                      Apply
                    </Button>
                    <Button
                      kind="secondary"
                      size="small"
                      onClick={() => cancelDisposedDateEdit(idx)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <CalendarPicker
                    title="Disposed Date"
                    fieldWidth={170}
                    value={
                      row.disposedDate ? new Date(row.disposedDate) : undefined
                    }
                    hideTitleOnInput
                    onChange={(val) => handleDisposedDateChange(idx, val)}
                    disabled={true}
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                  />
                  {isInEditMode && (
                    <Button
                      kind="secondary"
                      size="small"
                      onClick={() =>
                        openModal("showEditDisposalDate", () => {
                          beginDisposedDateEdit(idx, row.disposedDate);
                        })
                      }
                      disabled={isReadOnly || isAnotherRowDateEditInProgress}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              )
            ) : null}
          </td>
          <td style={{ width: "25%" }}>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              {!isReadOnly && (
                <>
                  {!isElectionSubmitted && (
                    <Button
                      className="btn btn-outline-danger"
                      onClick={() => handleDelete(idx, row)}
                      disabled={isDateEditInProgress}
                    >
                      Delete
                    </Button>
                  )}
                  {isElectionSubmitted && !isInEditMode && (
                    <Button
                      kind="secondary"
                      onClick={() =>
                        openModal("showMainEditDelete", () =>
                          setEditingRowIndex(idx),
                        )
                      }
                      disabled={isDateEditInProgress}
                    >
                      Edit/Delete
                    </Button>
                  )}
                  {isElectionSubmitted && isInEditMode && (
                    <Button
                      className="btn btn-outline-danger"
                      onClick={() =>
                        openModal("showDeletePartnership", () => {
                          handleDelete(idx, row);
                          setEditingRowIndex(null);
                        })
                      }
                      disabled={isDateEditInProgress}
                    >
                      Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      );
    },
    [
      applyStartDateEdit,
      beginStartDateEdit,
      cancelStartDateEdit,
      getDisplayName,
      canEdit,
      editingRowIndex,
      isReadOnly,
      handleNameChange,
      handleBlur,
      handleUTRChange,
      handleDateChange,
      applyDisposedDateEdit,
      beginDisposedDateEdit,
      cancelDisposedDateEdit,
      handleDisposedDateChange,
      handleDelete,
      openModal,
      editingStartDateIndex,
      editingDisposedDateIndex,
      updateStartDateDraft,
      updateStartDateMode,
      updateDisposedDateDraft,
      updateDisposedDateMode,
      startDateEditState,
      disposedDateEditState,
      allowedPoaPeriodEndDates,
      manualStartDateMax,
      firstModelStartDate,
    ],
  );

  return (
    <div className="mt-3">
      <h5>Consolidated Partnerships</h5>
      <table className="table ap-px-spacing-4" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th
              style={{
                width: isGroupDataPage ? "25%" : isGroupConfig ? "50%" : "50%",
                textAlign: "left",
              }}
            >
              {isGroupConfig
                ? renderHeadingWithTooltip(
                    "Consolidated partnerships",
                    HEADER_TOOLTIPS.name,
                  )
                : renderHeadingWithTooltip("Name", HEADER_TOOLTIPS.name)}
            </th>
            {isGroupDataPage && (
              <th
                style={{
                  width: "10%",
                  textAlign: "left",
                  verticalAlign: "top",
                }}
              >
                {renderHeadingWithTooltip("UTR", HEADER_TOOLTIPS.utr)}
              </th>
            )}
            {isGroupDataPage && (
              <th
                style={{
                  width: "20%",
                  textAlign: "left",
                  verticalAlign: "top",
                }}
              >
                {renderHeadingWithTooltip(
                  "Start Date",
                  HEADER_TOOLTIPS.startDate,
                )}
              </th>
            )}
            {isGroupDataPage && hasAnyElectionSubmitted && (
              <th
                style={{
                  width: "20%",
                  textAlign: "left",
                  verticalAlign: "top",
                }}
              >
                {renderHeadingWithTooltip(
                  "First period in which partnership is not present",
                  HEADER_TOOLTIPS.notPresent,
                )}
              </th>
            )}
            {showDateColumn && !isGroupDataPage && (
              <th style={{ width: "30%", textAlign: "left" }}>Date</th>
            )}
            <th style={{ width: isGroupDataPage ? "25%" : "20%" }}></th>
          </tr>
        </thead>
        <tbody>
          {normalizedPartnerships.length === 0 ? (
            <tr>
              <td
                colSpan={
                  isGroupDataPage
                    ? hasAnyElectionSubmitted
                      ? 5
                      : 4
                    : showDateColumn
                      ? 3
                      : 2
                }
                className="text-center text-muted"
              >
                No consolidated partnerships added
              </td>
            </tr>
          ) : (
            normalizedPartnerships.map((row, idx) => {
              if (isGroupDataPage) {
                return renderGroupDataRow(row, idx);
              }

              // Original rendering for Group Config
              const displayName = getDisplayName(idx, row.name);
              const isRowEditable = canEdit(row, idx);

              return (
                <React.Fragment key={`cp-${idx}`}>
                  <tr>
                    <td
                      style={{
                        width: isGroupConfig ? "50%" : "50%",
                        ...(isGroupConfig && showDatePicker
                          ? { display: "flex", alignItems: "center", gap: 8 }
                          : {}),
                      }}
                    >
                      <Input
                        className={isGroupConfig ? "col" : undefined}
                        title={
                          isGroupConfig
                            ? "Please provide consolidated partnership name"
                            : "Consolidated partnership name"
                        }
                        value={displayName}
                        hideTitleOnInput
                        onChange={(val) => handleNameChange(idx, val, row)}
                        onBlur={() => handleBlur(idx)}
                        disabled={isReadOnly || !isRowEditable}
                        error={isGroupConfig && !row.name}
                      />
                      {isGroupConfig && showDatePicker && (
                        <CalendarPicker
                          fieldTitle="Date"
                          fieldWidth={140}
                          format="DD/MM/YYYY"
                          placeholder="dd/mm/yyyy"
                          value={row.date ? new Date(row.date) : undefined}
                          error={!row.date}
                          onChange={(val) => {
                            if (val) handleDateChange(idx, val);
                          }}
                          disabled={isReadOnly}
                        />
                      )}
                    </td>
                    {showDateColumn && !isGroupConfig && (
                      <td style={{ width: "30%" }}>
                        {row.date
                          ? processDateJS(row.date).format("DD/MM/YYYY")
                          : ""}
                      </td>
                    )}
                    <td style={{ width: "20%" }}>
                      {!isReadOnly && isRowEditable && (
                        <Button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(idx, row)}
                        >
                          <span className="Appkit4-icon icon-close-outline"></span>
                        </Button>
                      )}
                    </td>
                  </tr>
                  {!isGroupConfig && (
                    <tr key={`cp-spacer-${idx}`} style={{ height: "8px" }}>
                      <td colSpan={showDateColumn ? 3 : 2}></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
          {!isReadOnly && (
            <tr>
              <td colSpan={isGroupDataPage ? 5 : showDateColumn ? 3 : 2}>
                <Button className="col" kind="secondary" onClick={handleAdd}>
                  Add consolidated partnership
                </Button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isGroupDataPage && (
        <>
          <EditStartDateWarningModal
            show={modalState.showEditStartDate}
            onConfirm={() => handleModalConfirm("showEditStartDate")}
            onCancel={() => closeModal("showEditStartDate")}
          />
          <MainEditDeleteWarningModal
            show={modalState.showMainEditDelete}
            onConfirm={() => handleModalConfirm("showMainEditDelete")}
            onCancel={() => closeModal("showMainEditDelete")}
          />
          <EditDisposalDateWarningModal
            show={modalState.showEditDisposalDate}
            onConfirm={() => handleModalConfirm("showEditDisposalDate")}
            onCancel={() => closeModal("showEditDisposalDate")}
          />
          <DeletePartnershipWarningModal
            show={modalState.showDeletePartnership}
            onConfirm={() => handleModalConfirm("showDeletePartnership")}
            onCancel={() => closeModal("showDeletePartnership")}
          />
        </>
      )}
    </div>
  );
};

export default ConsolidatedPartnerships;
export { normalizeConsolidatedPartnerships };
